import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

export default function DashboardPage({ setActiveTab, cabinetId = null }) {

  const [nomCabinet, setNomCabinet] = useState('MON CABINET')
  const [stats, setStats] = useState({
    projetsActifs: 0,
    honorairesMois: 0,
    honorairesAvenir: 0,
    pvAttente: 0,
    totalProjets: 0,
    clientsActifs: 0,
    projetsTermines: 0,
  })
  const [projets, setProjets] = useState([])
  const [jalons, setJalons] = useState([])
  const [loading, setLoading] = useState(true)

  const today = new Date().toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric',
    month: 'long', day: 'numeric'
  })

  useEffect(() => {
    const nom = localStorage.getItem('yoma_company_name')
    if (nom) setNomCabinet(nom)
    if (cabinetId == null) return
    loadData()
  }, [cabinetId])

  const loadData = async () => {
    setLoading(true)
    try {
      const { data: projetsData } = await supabase
        .from('projects')
        .select('*')
        .eq('cabinet_id', cabinetId)
        .order('created_at', { ascending: false })

      if (projetsData) {
        setProjets(projetsData)
        const actifs = projetsData.filter(p =>
          p.status !== 'termine' && p.status !== 'annule'
        )
        const termines = projetsData.filter(p => p.status === 'termine')
        setStats(s => ({
          ...s,
          projetsActifs: actifs.length,
          totalProjets: projetsData.length,
          projetsTermines: termines.length,
          clientsActifs: actifs.length,
        }))
      }

      const { data: jalonData } = await supabase
        .from('milestones')
        .select('*, projects(name)')
        .eq('cabinet_id', cabinetId)
        .order('due_date', { ascending: true })
        .limit(5)

      if (jalonData) setJalons(jalonData)

    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const C = {
    bg: '#F8F4EE',
    bgCard: '#FFFFFF',
    taupe: '#8B7361',
    taupeDeep: '#3D2E20',
    gold: '#C09A5B',
    goldLight: '#E8D4B0',
    border: '#DDD0C0',
    beige: '#EDE3D5',
    muted: '#9A8778',
    success: '#5C8A5C',
    alert: '#B85C3A',
  }

  const cardStyle = {
    background: C.bgCard,
    border: `1px solid ${C.border}`,
    borderRadius: '8px',
    padding: '20px',
  }

  const kpiCard = (label, value, color = C.taupeDeep, sub = '') => (
    <div style={{
      ...cardStyle,
      flex: 1,
      borderTop: `3px solid ${color}`,
    }}><div style={{
        fontFamily: 'DM Sans, sans-serif',
        fontSize: '10px',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: C.muted,
        marginBottom: '8px',
      }}>
        {label}
      </div><div style={{
        fontFamily: 'Playfair Display, serif',
        fontSize: '28px',
        fontWeight: 600,
        color,
        lineHeight: 1,
      }}>
        {loading ? '—' : value}
      </div>
      {sub && (
        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: '11px',
          color: C.muted,
          marginTop: '6px',
        }}>
          {sub}
        </div>
      )}
    </div>
  )

  const statutColor = (statut) => {
    if (!statut) return C.muted
    if (statut === 'conforme') return C.success
    if (statut === 'impaye') return C.alert
    if (statut === 'partiel') return C.gold
    return C.muted
  }

  if (cabinetId == null) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F4EE', padding: '32px', fontFamily: 'DM Sans, sans-serif', color: '#8B7361' }}>
        Chargement...
      </div>
    )
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: C.bg,
      padding: '32px',
      fontFamily: 'DM Sans, sans-serif',
    }}>

      {/* HEADER */}
      <div style={{ marginBottom: '32px' }}><div style={{
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: C.muted,
          marginBottom: '6px',
        }}>
          {nomCabinet} — CABINET
        </div><div style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: '32px',
          fontWeight: 500,
          color: C.taupeDeep,
          marginBottom: '4px',
        }}>
          Tableau de bord
        </div><div style={{ fontSize: '13px', color: C.muted }}>
          Vue d'ensemble de votre activité · {today}
        </div></div>

      {/* KPIs */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '24px',
        flexWrap: 'wrap',
      }}>
        {kpiCard('Mandats actifs', `${stats.projetsActifs} projets`, C.taupeDeep)}
        {kpiCard('Honoraires ce mois', `${stats.honorairesMois.toLocaleString('fr-FR')} MAD`, C.gold)}
        {kpiCard('Honoraires à venir', `${stats.honorairesAvenir.toLocaleString('fr-FR')} MAD`, C.gold)}
        {kpiCard('PV en attente', `${stats.pvAttente} à valider`, stats.pvAttente > 0 ? C.alert : C.success)}
      </div>

      {/* MILIEU */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* CA 6 mois */}
        <div style={cardStyle}><div style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: C.muted,
            marginBottom: '16px',
          }}>
            CA YOMA — 6 derniers mois
          </div><div style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '13px',
            color: C.muted,
            fontStyle: 'italic',
          }}>
            Aucune recette enregistrée
          </div></div>

        {/* Avancement projets */}
        <div style={cardStyle}><div style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: C.muted,
            marginBottom: '16px',
          }}>
            Avancement Projets
          </div>
          {projets.length === 0 ? (
            <div style={{ fontSize: '13px', color: C.muted, fontStyle: 'italic' }}>
              Aucun projet
            </div>
          ) : (
            projets.slice(0, 4).map(p => (
              <div key={p.id} style={{ marginBottom: '12px' }}><div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '4px',
                }}><span style={{ fontSize: '13px', color: C.taupeDeep }}>
                    {p.name}
                  </span><span style={{ fontSize: '12px', color: C.gold, fontWeight: 700 }}>
                    {p.progress || 0}%
                  </span></div><div style={{
                  height: '4px',
                  background: C.beige,
                  borderRadius: '2px',
                }}><div style={{
                    height: '100%',
                    width: `${p.progress || 0}%`,
                    background: C.gold,
                    borderRadius: '2px',
                    transition: 'width 0.3s',
                  }} /></div></div>
            ))
          )}
        </div></div>

      {/* BAS */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
        marginBottom: '24px',
      }}>
        {/* Prochains jalons */}
        <div style={cardStyle}><div style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: C.muted,
            marginBottom: '16px',
          }}>
            Prochains Jalons
          </div>
          {jalons.length === 0 ? (
            <div style={{ fontSize: '13px', color: C.muted, fontStyle: 'italic' }}>
              Aucun jalon à venir
            </div>
          ) : (
            jalons.slice(0, 4).map(j => (
              <div key={j.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: `1px solid ${C.border}`,
              }}><div><div style={{ fontSize: '13px', color: C.taupeDeep }}>
                    {j.name}
                  </div><div style={{ fontSize: '11px', color: C.muted }}>
                    {j.projects?.name}
                  </div></div><div style={{
                  fontSize: '11px',
                  color: C.gold,
                  fontWeight: 600,
                }}>
                  {j.due_date ? new Date(j.due_date)
                    .toLocaleDateString('fr-FR') : '—'}
                </div></div>
            ))
          )}
        </div>

        {/* PV en attente */}
        <div style={cardStyle}><div style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: C.muted,
            marginBottom: '16px',
          }}>
            PV en attente de validation
          </div><div style={{ fontSize: '13px', color: C.muted, fontStyle: 'italic' }}>
            Aucun PV en attente ✓
          </div></div></div>

      {/* PIED DE PAGE */}
      <div style={{
        display: 'flex',
        gap: '24px',
        padding: '16px 20px',
        background: C.beige,
        borderRadius: '6px',
        fontSize: '12px',
        color: C.muted,
        fontFamily: 'DM Sans, sans-serif',
      }}><span> {stats.totalProjets} projet{stats.totalProjets > 1 ? 's' : ''} total</span><span> {stats.clientsActifs} client{stats.clientsActifs > 1 ? 's' : ''} actif{stats.clientsActifs > 1 ? 's' : ''}</span><span>✓ {stats.projetsTermines} terminé{stats.projetsTermines > 1 ? 's' : ''}</span><span>✓ Tous les jalons à jour</span></div>
      {!loading && projets.length === 0 && (
        <div style={{ marginTop: '16px', padding: '16px 20px', background: '#FFFFFF', border: `1px solid ${C.border}`, borderRadius: '8px', fontFamily: 'DM Sans, sans-serif', color: C.taupeDeep }}>
          Bienvenue ! Créez votre premier projet pour commencer.
        </div>
      )}
    </div>
  )
}